import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal, Box, Backdrop, Button, Typography, CircularProgress } from '@mui/material';
import { Close } from '@mui/icons-material';
import classes from '../Invoice.module.scss';
import sampleBulkInvoice from './sampleBulkInvoice';
import config from '../../../constants/config';
import { DataPostApiV2 } from '../../../helpers/PostDataApi';
import { FlexAlerts, AlertSeverityTypes } from '../../../components/Message/Alerts/Alerts';

const BatchInvoicesModal = (props) => {
  const { isOpen, onClose, locationId } = props;
  const [isLoading, setIsLoading] = useState(false);
  const [isValidUpload, setIsValidUpload] = useState(false);
  const [messages, setMessages] = useState([]);
  const [tempInvoices, setTempInvoices] = useState([]);

  const isValidDate = (dateString) => {
    // Validate date format (mm/dd/yy or mm/dd/yyyy)
    const regex = /^(0?[1-9]|1[0-2])\/(0?[1-9]|[12]\d|3[01])\/(19|20)?\d{2}$/;
    return regex.test(dateString);
  };

  const isValidAlphaNumeric = (input) => {
    const result = /^[a-zA-Z0-9/(),@.\-&'*’]+(?:\s+[a-zA-Z0-9/(),@.\-&'*’]+)*$/.test(input);
    return result;
  };

  const isValidEmail = (email) => {
    const regex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regex.test(email);
  };

  const checkRequiredColumns = (row, rowNumber, validationErrors) => {
    if (!row[1] || !row[2] || !row[3]) {
      pushValidationError(validationErrors, true, rowNumber, 'Required columns are missing.');
      return false;
    }
    return true;
  };

  const validateInvoice = (value) => {
    return value && !isValidAlphaNumeric(value);
  };

  const validateDate = (value) => {
    return !isValidDate(value);
  };

  const validateAlphaNumeric = (value) => {
    return !isValidAlphaNumeric(value);
  };

  const validateNumericRequired = (value) => {
    return isNaN(parseFloat(value));
  };
  const validateNumeric = (value) => {
    return value && isNaN(parseFloat(value));
  };
  const validatePaymentMethodType = (value) => {
    return value && !/^(card|ach)$/i.test(value);
  };

  const validateBoolean = (value) => {
    return value && !/^(true|false)$/i.test(value);
  };

  const validateLength = (value, maxLength) => {
    return value && value.length > maxLength;
  };

  const validateEmail = (value) => {
    return value && !isValidEmail(value);
  };

  const pushValidationError = (errorsArray, isError, rowNumber, message) => {
    if (isError) {
      errorsArray.push(`Row ${rowNumber}: ${message}`);
    }
  };

  const validateCSVData = (dataArray) => {
    const validationErrors = [];
    for (let i = 0; i < dataArray.length; i++) {
      const row = dataArray[i];
      const rowNumber = i + 1;
      // Check if all required columns are present
      if (!checkRequiredColumns(row, rowNumber, validationErrors)) continue;
      // Validate invoice (Alphanumeric)
      pushValidationError(validationErrors, validateInvoice(row[0]), rowNumber, 'Invalid invoice format.');
      // Validate billing_date format (mm/dd/yy or mm/dd/yyyy)
      pushValidationError(validationErrors, validateDate(row[1]), rowNumber, 'Invalid billing_date format.');
      // Validate customer_id (Alphanumeric)
      pushValidationError(validationErrors, validateAlphaNumeric(row[2]), rowNumber, 'Invalid customer_id format.');
      // Validate base_amount (Numeric)
      pushValidationError(validationErrors, validateNumericRequired(row[3]), rowNumber, 'Invalid base_amount format.');
      // Validate surcharge_amount, tax_amount, custom_fee_amount (Numeric)
      for (let j = 4; j <= 6; j++) {
        pushValidationError(validationErrors, validateNumeric(row[j]), rowNumber, `Invalid value at column ${j + 1}.`);
      }
      // Validate total_amount if provided
      pushValidationError(validationErrors, validateNumeric(row[7]), rowNumber, 'Invalid total_amount format.');
      // Validate payment_method_type if provided
      pushValidationError(
        validationErrors,
        validatePaymentMethodType(row[8]),
        rowNumber,
        'Invalid payment_method_type.'
      );
      // Validate surcharge_exempt, tax_exempt, Apply_tax_last, Custom_fee_exempt, email_receipt (Boolean)
      for (let j = 9; j <= 13; j++) {
        pushValidationError(validationErrors, validateBoolean(row[j]), rowNumber, `Invalid value at column ${j + 1}.`);
      }
      // Validate public_notes length
      pushValidationError(
        validationErrors,
        validateLength(row[14], 100),
        rowNumber,
        'Length of public_notes exceeds 100 characters.'
      );
      // Validate po_number length
      pushValidationError(
        validationErrors,
        validateLength(row[17], 17),
        rowNumber,
        'Length of po_number exceeds 17 characters.'
      );
      // Validate email_address format
      pushValidationError(validationErrors, validateEmail(row[18]), rowNumber, 'Invalid email_address format.');
      // Validate address fields length (billing_address_line_1, billing_address_line_2, ...)
      for (let j = 19; j <= 28; j++) {
        pushValidationError(
          validationErrors,
          validateLength(row[j], 100),
          rowNumber,
          `Length of address field at column ${j + 1} exceeds 100 characters.`
        );
      }
    }

    return validationErrors;
  };

  const handleFileChange = (e) => {
    setMessages([]);

    const file = e.target.files[0];
    const fileExtension = file?.name.split('.').pop().toLowerCase();
    const acceptedMimeTypes = [
      'text/csv',
      'application/csv',
      'application/vnd.ms-excel',
      'text/comma-separated-values',
      'application/vnd.oasis.opendocument.spreadsheet',
    ];

    if (!file || fileExtension !== 'csv' || !acceptedMimeTypes.includes(file.type)) {
      setTempInvoices([]);
      setIsValidUpload(false);
      setMessages([
        { message: 'File format not accepted. Please upload a CSV file.', severity: AlertSeverityTypes.error },
      ]);
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      let dataArray = text.split('\n').map((row) => row.split(','));
      dataArray = dataArray.filter((row) => row.length > 1 && row.some((cell) => cell.trim().length > 0)); // Remove empty arrays
      dataArray.shift();

      setTempInvoices(dataArray);

      const validationErrors = validateCSVData(dataArray);
      if (validationErrors.length === 0) {
        setIsValidUpload(true);
      } else {
        // Handle validation errors
        setIsValidUpload(false);
        const errorMessages = validationErrors?.map((error) => {
          return { message: error, severity: AlertSeverityTypes.error };
        });
        setMessages(errorMessages);
      }
    };
    reader.readAsText(file);
  };

  const handleDownloadTemplate = () => {
    const csvContent = 'data:text/csv;charset=utf-8,' + encodeURIComponent(sampleBulkInvoice);
    const link = document.createElement('a');
    link.setAttribute('href', csvContent);
    link.setAttribute('download', 'Sample Bulk Invoice.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleUploadInvoices = () => {
    if (!tempInvoices || tempInvoices.length === 0) {
      setMessages([{ message: 'No data to upload', severity: AlertSeverityTypes.warning }]);
      return;
    }

    if (isValidUpload) {
      setIsLoading(true);
      const invoices = tempInvoices.map((invoice) => ({
        invoice: invoice[0] ? invoice[0] : '',
        billing_date: invoice[1],
        customer_id: invoice[2],
        base_amount: invoice[3],
        surcharge_amount: invoice[4] ? invoice[4] : 0,
        tax_amount: invoice[5] ? invoice[5] : 0,
        custom_fee_amount: invoice[6] ? invoice[6] : 0,
        total_amount: invoice[7] ? invoice[7] : 0,
        payment_method_type: invoice[8] ? invoice[8] : 'card',
        tax_exempt: invoice[9]?.toUpperCase() === 'TRUE',
        apply_tax_last: invoice[10]?.toUpperCase() === 'TRUE',
        custom_fee_exempt: invoice[11]?.toUpperCase() === 'TRUE',
        surcharge_exempt: invoice[12]?.toUpperCase() === 'TRUE',
        email_receipt: invoice[13]?.toUpperCase() === 'TRUE',
        public_notes: invoice[14] ? invoice[14] : '',
        first_name: invoice[15] ? invoice[15] : '',
        last_name: invoice[16] ? invoice[16] : '',
        po_number: invoice[17] ? invoice[17] : '',
        customer_email: invoice[18],
        customer_billing_address_1: invoice[19],
        customer_billing_address_2: invoice[20],
        customer_billing_city: invoice[21],
        customer_billing_state: invoice[22],
        customer_billing_zip: invoice[23],
        customer_shipping_address_1: invoice[24],
        customer_shipping_address_2: invoice[25],
        customer_shipping_city: invoice[26],
        customer_shipping_state: invoice[27],
        customer_shipping_zip: invoice[28],
      }));

      let userDetails = localStorage.getItem('user');
      userDetails = JSON.parse(userDetails) || {};
      DataPostApiV2(config.BASEURL + 'uploadBulkInvoices', {
        locationId,
        userDetails,
        validInvoices: invoices,
      })
        .then((response) => {
          setMessages([{ message: response?.data?.message, severity: AlertSeverityTypes.success }]);
        })
        .catch((error) => {
          setMessages([{ message: error?.response?.data?.message, severity: AlertSeverityTypes.error }]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  };

  return (
    <Modal open={isOpen} aria-labelledby="modal-modal-title" aria-describedby="modal-modal-description">
      <Box className="bulkInvoicesModal" style={{ overflowY: 'auto' }}>
        <Backdrop open={isLoading}>
          <CircularProgress style={{ color: 'white' }} />
        </Backdrop>
        <FlexAlerts messages={messages} />
        <Close style={{ position: 'absolute', right: 25, cursor: 'pointer', marginBottom: '10px' }} onClick={onClose} />
        {tempInvoices.length === 0 && (
          <>
            <h3>Upload Files</h3>

            <div className="drop_box">
              <header>
                <h4>Select File here</h4>
              </header>
              <p> Files Supported: CSV, Max Size: 20MB</p>
              <Typography
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  display: 'flex',
                  fontSize: 18,
                  fontWeight: '700',
                }}
              ></Typography>
              <div
                className="file-upload-wrapper"
                data-text="Select your file."
                style={{ justifyContent: 'center', alignItems: 'center', display: 'flex' }}
              >
                <input
                  type="file"
                  name="file"
                  multiple={isOpen}
                  accept={'.csv'}
                  id="fileID"
                  onChange={handleFileChange}
                  className="input-class file-upload-field "
                />
              </div>
            </div>
            <Button
              id="saveSampleFile"
              type="button"
              variant="contained"
              color="primary"
              className={classes.submit}
              onClick={handleDownloadTemplate}
            >
              Download Template
            </Button>
          </>
        )}
        {tempInvoices.length !== 0 && (
          <>
            <header>
              <h4>Selected Invoices</h4>
            </header>
            <Typography
              style={{
                justifyContent: 'center',
                alignItems: 'center',
                display: 'flex',
                fontSize: 18,
                fontWeight: '700',
              }}
            ></Typography>
            {tempInvoices.length > 0 && (
              <div className="bulk_table_display ">
                <table className="csv-table">
                  <thead>
                    <tr>
                      <th>Invoice Number</th>
                      <th>Billing Date</th>
                      <th>Customer ID</th>
                      <th>Base Amount</th>
                      <th>Surcharge Amount</th>
                      <th>Tax Amount</th>
                      <th>Custom Fee Amount</th>
                      <th>Total Amount</th>
                      <th>Payment Method Type</th>
                      <th>Tax Exempt</th>
                      <th>Apply Tax Last</th>
                      <th>Custom Fee Exempt</th>
                      <th>Surcharge Exempt</th>
                      <th>Email Receipt</th>
                      <th>Public Notes</th>
                      <th>First Name</th>
                      <th>Last Name</th>
                      <th>PO Number</th>
                      <th>Customer Email</th>
                      <th>Customer Billing Address 1</th>
                      <th>Customer Billing Address 2</th>
                      <th>Customer Billing City</th>
                      <th>Customer Billing State</th>
                      <th>Customer Billing Zip</th>
                      <th>Customer Shipping Address 1</th>
                      <th>Customer Shipping Address 2</th>
                      <th>Customer Shipping City</th>
                      <th>Customer Shipping State</th>
                      <th>Customer Shipping Zip</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tempInvoices.map((row, rowIndex) => (
                      <tr key={rowIndex}>
                        {Object.values(row).map((cell, cellIndex) => (
                          <td key={cellIndex}>{cell}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div>
              <Button
                id="downloadSampleFile"
                type="button"
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={handleDownloadTemplate}
                style={{ marginTop: '10px' }}
              >
                Download Template
              </Button>
              <Button
                id="saveSampleFile"
                type="button"
                variant="contained"
                color="primary"
                className={classes.submit}
                onClick={handleUploadInvoices}
                style={{ marginTop: '10px', marginLeft: '10px' }}
                disabled={!isValidUpload}
              >
                Upload Invoices
              </Button>
            </div>
          </>
        )}
      </Box>
    </Modal>
  );
};

BatchInvoicesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
  locationId: PropTypes.string,
};

export default BatchInvoicesModal;
